
import { Component, Prop } from 'vue-property-decorator';
import Page from '@/Page.vue';
import { TenantUserModel, CategoryUserModel, PermissionTypeEnum, RoleModel } from '@/libs/Api';
import { rolesStore } from '@/libs/roles/+state/store';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';

@Component({})
export default class PermissionsOverview extends Page {
    @Prop() tenantUser!: TenantUserModel;
    @Prop() allowedCategories!: CategoryUserModel[];

    // Cache pro role - načteno jednou při vytvoření komponenty
    private cachedRoles: RoleModel[] = [];
    private rolesMap: Map<string, RoleModel> = new Map();
    private permissionsCache: Map<string, PermissionTypeEnum[]> = new Map();

    created() {
        // Načíst role jednou při vytvoření komponenty
        this.cachedRoles = (rolesStore.useGetter(CrudGetter.Data) as RoleModel[]) || [];
        // Vytvořit mapu pro rychlý přístup
        this.cachedRoles.forEach((role) => {
            if (role.id) {
                this.rolesMap.set(role.id, role);
            }
        });
    }

    get globalRoles(): RoleModel[] {
        const roles = this.tenantUser?.userRoles?.map((ur) => ur.role!).filter((r) => r) ?? [];
        // Seřadit role abecedně podle názvu
        return roles.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'cs'));
    }

    get globalPermissions(): PermissionTypeEnum[] {
        const perms: PermissionTypeEnum[] = [];
        this.globalRoles.forEach((role) => {
            role.permissions?.forEach((p) => {
                if (!perms.includes(p.permissionType!)) {
                    perms.push(p.permissionType!);
                }
            });
        });
        // Seřadit abecedně podle přeloženého názvu
        return this.sortPermissions(perms);
    }

    get allUniquePermissions(): PermissionTypeEnum[] {
        const perms = new Set<PermissionTypeEnum>(this.globalPermissions);

        if (!this.tenantUser.accessAllCategories) {
            this.allowedCategories
                .filter((c) => !c.inheritedRoles)
                .forEach((catUser) => {
                    this.getCategoryPermissions(catUser).forEach((p) => perms.add(p));
                });
        }

        // Seřadit abecedně podle přeloženého názvu
        return this.sortPermissions(Array.from(perms));
    }

    // Pomocná metoda pro sortování oprávnění
    private sortPermissions(perms: PermissionTypeEnum[]): PermissionTypeEnum[] {
        return perms.sort((a, b) => {
            const nameA = this.$t(`permissions.${a}`).toString();
            const nameB = this.$t(`permissions.${b}`).toString();
            return nameA.localeCompare(nameB, 'cs');
        });
    }

    getCategoryRoles(categoryUser: CategoryUserModel): any[] {
        const roles = categoryUser.categoryUserRoles ?? [];

        // Seřadit role abecedně podle názvu
        return [...roles].sort((a, b) => {
            const roleA = this.rolesMap.get(a.roleId ?? '');
            const roleB = this.rolesMap.get(b.roleId ?? '');
            const nameA = roleA?.name ?? '';
            const nameB = roleB?.name ?? '';
            return nameA.localeCompare(nameB, 'cs');
        });
    }

    getCategoryPermissions(categoryUser: CategoryUserModel): PermissionTypeEnum[] {
        // Použít cache pro výkon
        const cacheKey = `cat_${categoryUser.id}`;
        if (this.permissionsCache.has(cacheKey)) {
            return this.permissionsCache.get(cacheKey)!;
        }

        const perms: PermissionTypeEnum[] = [];

        categoryUser.categoryUserRoles?.forEach((cur) => {
            // Najít roli v cachedRoles podle roleId (server neposílá expanded role)
            const role = this.rolesMap.get(cur.roleId ?? '');
            if (role?.permissions) {
                role.permissions.forEach((p) => {
                    if (p.permissionType && !perms.includes(p.permissionType)) {
                        perms.push(p.permissionType);
                    }
                });
            }
        });

        // Seřadit abecedně podle přeloženého názvu
        const sorted = this.sortPermissions(perms);
        this.permissionsCache.set(cacheKey, sorted);
        return sorted;
    }

    getRoleName(roleId?: string): string {
        if (!roleId) return '';
        return this.rolesMap.get(roleId)?.name ?? '';
    }

    getRolePermissions(role: RoleModel): PermissionTypeEnum[] {
        // Použít cache pro výkon
        const cacheKey = `role_${role.id}`;
        if (this.permissionsCache.has(cacheKey)) {
            return this.permissionsCache.get(cacheKey)!;
        }

        const perms: PermissionTypeEnum[] = [];
        role.permissions?.forEach((p) => {
            if (p.permissionType) {
                perms.push(p.permissionType);
            }
        });

        // Seřadit abecedně podle přeloženého názvu
        const sorted = this.sortPermissions(perms);
        this.permissionsCache.set(cacheKey, sorted);
        return sorted;
    }

    getRolePermissionsById(roleId?: string): PermissionTypeEnum[] {
        if (!roleId) return [];
        const role = this.rolesMap.get(roleId);
        if (!role) return [];
        return this.getRolePermissions(role);
    }
}
